<template>
  <form
    class="ai-form"
    @submit.prevent="submit"
  >
    <div class="ai-form__intro">
      <h3>Новое расследование</h3>
      <p>
        Ассистент сам изучит данные в заданных рамках и соберёт отчёт с доказательствами. Это может занять несколько
        минут.
      </p>
    </div>

    <div class="ai-field">
      <span class="ai-field__label">Сценарий</span>
      <div class="ai-chips">
        <button
          v-for="item in scenarios"
          :key="item.key"
          type="button"
          class="ai-chip-button"
          :class="{ 'is-active': scenario === item.key }"
          @click="scenario = item.key"
        >
          {{ item.title }}
        </button>
      </div>
    </div>

    <label class="ai-field">
      <span class="ai-field__label">Что нужно выяснить</span>
      <textarea
        v-model="text"
        class="ai-input"
        rows="3"
        maxlength="4000"
        :placeholder="placeholder"
      />
    </label>

    <div class="ai-field">
      <span class="ai-field__label">Период</span>
      <div class="ai-chips">
        <button
          v-for="preset in presets"
          :key="preset.days"
          type="button"
          class="ai-chip-button"
          :class="{ 'is-active': days === preset.days }"
          @click="setDays(preset.days)"
        >
          {{ preset.label }}
        </button>
      </div>
      <div class="ai-form__dates">
        <input
          v-model="dateFrom"
          type="date"
          class="ai-input"
          :max="dateTo || today"
          @input="days = null"
        >
        <span>—</span>
        <input
          v-model="dateTo"
          type="date"
          class="ai-input"
          :min="dateFrom"
          :max="today"
          @input="days = null"
        >
      </div>
    </div>

    <div class="ai-field">
      <span class="ai-field__label">
        Компьютеры
        <span class="ai-field__hint">выбрано {{ computerIds.length }} из {{ computers.length }}</span>
      </span>
      <div class="ai-form__toolbar">
        <input
          v-model="filter"
          type="search"
          class="ai-input"
          placeholder="Поиск по сотруднику, компьютеру или группе"
        >
        <button
          type="button"
          class="ai-link-button"
          @click="toggleAll(visible)"
        >
          {{ allSelected(visible) ? 'Снять' : 'Выбрать все' }}
        </button>
      </div>
      <div class="ai-form__box">
        <p
          v-if="computersError"
          class="ai-form__error"
        >
          {{ computersError }}
        </p>
        <p
          v-else-if="!computersLoaded"
          class="ai-form__muted"
        >
          <span class="ai-spinner ai-spinner--small" />
          Загружаю список…
        </p>
        <template v-for="group in groups">
          <label
            :key="`g${group.name}`"
            class="ai-check is-group"
          >
            <input
              type="checkbox"
              :checked="allSelected(group.items)"
              @change="toggleAll(group.items)"
            >
            <span>{{ group.name || 'Без группы' }}</span>
            <span class="ai-field__hint">{{ group.items.length }}</span>
          </label>
          <label
            v-for="computer in group.items"
            :key="computer.id"
            class="ai-check"
          >
            <input
              v-model="computerIds"
              type="checkbox"
              :value="computer.id"
            >
            <span>{{ computer.user || computer.host }}</span>
            <span
              v-if="computer.user"
              class="ai-field__hint"
            >
              {{ computer.host }}
            </span>
          </label>
        </template>
        <p
          v-if="computersLoaded && !groups.length"
          class="ai-form__muted"
        >
          Ничего не найдено.
        </p>
      </div>
    </div>

    <div class="ai-field">
      <span class="ai-field__label">
        Источники данных
        <span class="ai-field__hint">выбрано {{ sources.length }} из {{ allSources.length }}</span>
        <button
          type="button"
          class="ai-link-button"
          @click="sources = sources.length === allSources.length ? [] : allSources.map((s) => s.slug)"
        >
          {{ sources.length === allSources.length ? 'Снять' : 'Выбрать все' }}
        </button>
      </span>
      <div class="ai-form__sources">
        <fieldset
          v-for="group in sourceGroups"
          :key="group.key"
        >
          <legend>{{ group.label }}</legend>
          <label
            v-for="source in group.items"
            :key="source.slug"
            class="ai-check"
          >
            <input
              v-model="sources"
              type="checkbox"
              :value="source.slug"
            >
            <span>{{ source.label }}</span>
          </label>
        </fieldset>
      </div>
    </div>

    <p
      v-if="problem || error"
      class="ai-form__error"
    >
      {{ error || problem }}
    </p>
    <div class="ai-form__actions">
      <button
        type="button"
        class="ai-button"
        @click="$emit('cancel')"
      >
        Отмена
      </button>
      <button
        type="submit"
        class="ai-button is-primary"
        :disabled="!!problem || sending"
      >
        <span
          v-if="sending"
          class="ai-spinner ai-spinner--small"
        />
        Начать расследование
      </button>
    </div>
  </form>
</template>

<script>
  import dayjs from 'dayjs';
  import { errorText } from '@/api/methods/assistant';

  const PRESETS = [
    { days: 1, label: 'Сегодня' },
    { days: 7, label: '7 дней' },
    { days: 30, label: '30 дней' },
    { days: 90, label: '90 дней' },
  ];
  const HINTS = {
    document: 'Например: куда мог уйти прайс-лист на 2026 год, кто открывал и пересылал файл',
    alert: 'Например: разобрать срабатывания политик за период и оценить, есть ли реальный риск',
    transfer: 'Например: кто копировал файлы на флешки или отправлял их через облака и почту',
    sidework: 'Например: не работал ли сотрудник над проектами сторонних заказчиков в рабочее время',
    custom: 'Опишите, что произошло и что нужно проверить',
  };
  const DAY = 'YYYY-MM-DD';

  export default {
    name: 'InvestigationForm',
    props: {
      capabilities: { type: Object, required: true },
      sending: { type: Boolean, default: false },
      error: { type: String, default: '' },
    },
    data() {
      const today = dayjs().format(DAY);
      const slugs = (this.capabilities.sources || []).map((s) => s.slug);
      return {
        scenario: 'custom',
        text: '',
        days: 30,
        today,
        dateFrom: dayjs().subtract(29, 'day').format(DAY),
        dateTo: today,
        filter: '',
        computers: [],
        computersLoaded: false,
        computersError: '',
        computerIds: [],
        sources: slugs,
        presets: PRESETS,
      };
    },
    computed: {
      scenarios() {
        return this.capabilities.scenarios || [];
      },
      allSources() {
        return this.capabilities.sources || [];
      },
      sourceGroups() {
        return (this.capabilities.groups || [])
          .map((group) => ({ ...group, items: this.allSources.filter((s) => s.group === group.key) }))
          .filter((group) => group.items.length);
      },
      placeholder() {
        return HINTS[this.scenario] || HINTS.custom;
      },
      visible() {
        const needle = this.filter.trim().toLowerCase();
        if (!needle) return this.computers;
        return this.computers.filter((c) =>
          [c.user, c.host, c.group].some((v) => (v || '').toLowerCase().includes(needle)),
        );
      },
      groups() {
        const result = [];
        const index = {};
        this.visible.forEach((computer) => {
          const name = computer.group || '';
          if (!(name in index)) {
            index[name] = result.length;
            result.push({ name, items: [] });
          }
          result[index[name]].items.push(computer);
        });
        return result;
      },
      limits() {
        return this.capabilities.limits || {};
      },
      problem() {
        if (!this.text.trim()) return 'Опишите, что нужно выяснить.';
        if (!this.dateFrom || !this.dateTo || this.dateFrom > this.dateTo) return 'Укажите корректный период.';
        const span = dayjs(this.dateTo).diff(dayjs(this.dateFrom), 'day') + 1;
        if (span > (this.limits.investigationPeriodDays || 366)) {
          return `Период не может быть длиннее ${this.limits.investigationPeriodDays || 366} дней.`;
        }
        if (!this.computerIds.length) return 'Выберите хотя бы один компьютер.';
        if (this.computerIds.length > (this.limits.investigationComputers || 500)) {
          return `Можно выбрать не больше ${this.limits.investigationComputers || 500} компьютеров.`;
        }
        if (!this.sources.length) return 'Выберите хотя бы один источник данных.';
        return '';
      },
    },
    async created() {
      try {
        const data = await this.$store.dispatch('assistant/loadComputers');
        this.computers = data.computers || [];
        if (!this.computerIds.length && this.computers.length <= 20) {
          this.computerIds = this.computers.map((c) => c.id);
        }
      } catch (error) {
        this.computersError = errorText(error, 'Не удалось загрузить список компьютеров');
      } finally {
        this.computersLoaded = true;
      }
    },
    methods: {
      setDays(days) {
        this.days = days;
        this.dateTo = this.today;
        this.dateFrom = dayjs()
          .subtract(days - 1, 'day')
          .format(DAY);
      },
      allSelected(items) {
        return items.length > 0 && items.every((c) => this.computerIds.includes(c.id));
      },
      toggleAll(items) {
        const ids = items.map((c) => c.id);
        this.computerIds = this.allSelected(items)
          ? this.computerIds.filter((id) => !ids.includes(id))
          : [...new Set([...this.computerIds, ...ids])];
      },
      submit() {
        if (this.problem || this.sending) return;
        this.$emit('submit', {
          text: this.text.trim(),
          scope: {
            scenario: this.scenario,
            dateFrom: this.dateFrom,
            dateTo: this.dateTo,
            computerIds: this.computerIds,
            sources: this.sources,
          },
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-form {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 14px;
    min-height: 0;
    padding: 16px 18px;
    overflow-y: auto;
  }

  .ai-form__intro {
    h3 {
      margin: 0 0 4px;
      color: var(--ui-text);
      font-size: 16px;
      font-weight: 500;
    }

    p {
      margin: 0;
      color: var(--ui-text-muted);
      font-size: 13px;
    }
  }

  .ai-form__dates,
  .ai-form__toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;

    .ai-input {
      flex: 1;
      min-width: 0;
    }
  }

  .ai-form__box {
    max-height: 220px;
    margin-top: 8px;
    padding: 6px 10px;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius-sm);
    overflow-y: auto;
  }

  .ai-form__sources {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 8px 16px;
    margin-top: 6px;

    fieldset {
      margin: 0;
      padding: 0;
      border: 0;
    }

    legend {
      margin-bottom: 2px;
      color: var(--ui-text-muted);
      font-size: 12px;
    }
  }

  .ai-form__muted,
  .ai-form__error {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 4px 0;
    color: var(--ui-text-muted);
    font-size: 13px;
  }

  .ai-form__error {
    color: var(--ui-danger);
  }

  .ai-form__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 4px;
  }
</style>
